import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { useGo } from '../components/Nav';
import { Spinner } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { startDesktopHandoff } from '../queries/device';

/**
 * The bounce that signs Slate for Mac, and Slate for Chrome, in.
 *
 * The app opens this page with a `state` it made up. If nobody is signed in
 * here, we go through login or signup and come straight back. Once someone
 * is, we ask the API for a code already approved for them and send the
 * browser to slate://auth with that code and the same state, which is the
 * app's cue to collect its token. The code is dead two minutes later and
 * spent after one use, so the URL is not worth keeping.
 *
 * The Chrome extension has no URL scheme. It opens this page in Chrome's own
 * sign-in window (chrome.identity) and passes `redirect`, the address Chrome
 * gave it for that window, which is always https://<extension id>.chromiumapp.org/.
 * Sending the code there closes the window and hands the code to the
 * extension, and nobody else can register that hostname.
 */
/**
 * Each build of the app registers its own scheme (local, dev, prod) so a
 * machine with all three sends the code to the one that asked. Anything not
 * on this list is treated as the real app.
 */
const SCHEMES = new Set(['slate', 'slate-dev', 'slate-local']);

/** Which app is asking, for the name it gets in the session list. */
const APP_NAMES: Record<string, string> = {
  mac: 'Slate for Mac',
  windows: 'Slate for Windows',
  chrome: 'Slate for Chrome',
};

/** The one shape of address the extension may ask us to send the code to. */
const EXTENSION_REDIRECT = /^https:\/\/[a-p]{32}\.chromiumapp\.org\//;

export function DesktopPage({
  state,
  signup,
  scheme,
  app,
  redirect,
}: {
  state: string;
  signup: boolean;
  scheme: string;
  app?: string;
  redirect?: string;
}) {
  const appScheme = SCHEMES.has(scheme) ? scheme : 'slate';
  const appName = APP_NAMES[app ?? 'mac'] ?? APP_NAMES.mac;
  const isExtension = app === 'chrome' && Boolean(redirect && EXTENSION_REDIRECT.test(redirect));
  const { user, loading } = useAuth();
  const go = useGo();
  const [launched, setLaunched] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const here =
    `/desktop?state=${encodeURIComponent(state)}&scheme=${encodeURIComponent(appScheme)}` +
    `${app ? `&app=${encodeURIComponent(app)}` : ''}${isExtension && redirect ? `&redirect=${encodeURIComponent(redirect)}` : ''}`;

  useEffect(() => {
    if (loading || user) return;
    go(`${signup ? '/signup' : '/login'}?next=${encodeURIComponent(here)}`, { replace: true });
  }, [go, here, loading, signup, user]);

  useEffect(() => {
    if (!user || launched || error) return;
    let cancelled = false;
    startDesktopHandoff(appName)
      .then(({ deviceCode }) => {
        if (cancelled) return;
        const query = `code=${encodeURIComponent(deviceCode)}&state=${encodeURIComponent(state)}`;
        const target = isExtension && redirect ? `${redirect}?${query}` : `${appScheme}://auth?${query}`;
        track('Desktop Sign In Started', { intent: signup ? 'sign_up' : 'sign_in' });
        setLaunched(target);
        window.location.href = target;
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(errorMessage(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [appName, appScheme, error, isExtension, launched, redirect, signup, state, user]);

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-950 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Wordmark />
        </div>
        <div className="card overflow-hidden">
          <ClapperRule />
          <div className="p-6 text-center">
            {error ? (
              <>
                <h1 className="text-h3 text-chalk">{appName} could not be signed in</h1>
                <p className="mt-2 text-body text-chalk-dim">{error}</p>
                <button
                  type="button"
                  className="mt-4 text-body text-signal hover:underline"
                  onClick={() => setError(null)}
                >
                  Try again
                </button>
              </>
            ) : launched && isExtension ? (
              <>
                <h1 className="text-h3 text-chalk">Slate for Chrome is signed in</h1>
                <p className="mt-2 text-body text-chalk-dim">
                  This window closes by itself. If it is still here, close it and click the Slate button again.
                </p>
              </>
            ) : launched ? (
              <>
                <h1 className="text-h3 text-chalk">Launching Slate</h1>
                <p className="mt-2 text-body text-chalk-dim">
                  If you are not taken back to the app,{' '}
                  <a href={launched} className="text-signal hover:underline">
                    click here
                  </a>
                  .
                </p>
                <p className="mt-4 text-caption text-chalk-faint">
                  You can close this tab afterwards. Recordings will land in your library.
                </p>
              </>
            ) : (
              <div className="flex items-center justify-center gap-2.5 py-4 text-chalk-faint">
                <Spinner />
                <span className="text-body">Signing {appName} in…</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
